/**
 * Realtime client: authenticated WebSocket with acknowledgements, heartbeats/RTT measurement,
 * exponential-backoff reconnection, online/offline awareness and resume hooks.
 */
import type { ServerEnvelope } from '@cardhub/protocol';
import { uuid } from '../lib/device';

export type WsStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'offline' | 'replaced' | 'closed';

type Handler = (data: unknown, env: ServerEnvelope) => void;

interface Pending {
  resolve: (v: { ok: boolean; data?: unknown; error?: { code: string; message: string } }) => void;
  timer: ReturnType<typeof setTimeout>;
}

const WS_URL = (import.meta.env.VITE_WS_URL as string | undefined) ?? `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;

export class RealtimeClient {
  private ws: WebSocket | null = null;
  private handlers = new Map<string, Set<Handler>>();
  private pending = new Map<string, Pending>();
  private statusListeners = new Set<(s: WsStatus) => void>();
  private reconnectListeners = new Set<() => void>();
  private attempts = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private wanted = false;
  status: WsStatus = 'idle';
  rttMs = 0;
  serverOffsetMs = 0;
  private everConnected = false;
  /** Last time anything arrived from the server (pongs included) — used to spot silently dead sockets. */
  private lastMessageAt = 0;
  private hiddenAt = 0;
  private openWaiters: (() => void)[] = [];
  /** Gives up on a connection attempt that neither opens nor fails (a network that dropped mid-handshake). */
  private handshakeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly getToken: () => Promise<string | null>) {
    window.addEventListener('online', () => {
      if (!this.wanted) return;
      // The network is back: don't wait out a long backoff, and replace any attempt that was hanging while it was down.
      this.attempts = 0;
      if (this.status !== 'open') this.connectNow();
      else if (this.looksStale) this.forceReconnect();
    });
    window.addEventListener('offline', () => this.setStatus('offline'));
    // Safety net: whatever went wrong (timers throttled in a background tab, a missed event), never sit disconnected for long.
    setInterval(() => {
      if (!this.wanted || this.status === 'open' || this.status === 'replaced' || this.status === 'closed') return;
      if (this.retryTimer || this.handshakeTimer) return;
      if (navigator.onLine) this.connectNow();
    }, 5000);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.hiddenAt = Date.now();
        return;
      }
      if (!this.wanted) return;
      // Phones freeze backgrounded apps: the socket can look "open" while the server has already dropped it.
      const away = this.hiddenAt ? Date.now() - this.hiddenAt : 0;
      this.hiddenAt = 0;
      if (this.status === 'open' && away > 2000) this.forceReconnect();
      else if (this.status !== 'open' && this.status !== 'connecting') this.connectNow();
    });
  }

  private setStatus(s: WsStatus): void {
    this.status = s;
    this.statusListeners.forEach((l) => l(s));
  }

  onStatus(fn: (s: WsStatus) => void): () => void {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  /** Called after every successful re-authentication following a drop (not the first connect). */
  onReconnected(fn: () => void): () => void {
    this.reconnectListeners.add(fn);
    return () => this.reconnectListeners.delete(fn);
  }

  on(type: string, fn: Handler): () => void {
    const set = this.handlers.get(type) ?? new Set();
    set.add(fn);
    this.handlers.set(type, set);
    return () => set.delete(fn);
  }

  start(): void {
    this.wanted = true;
    if (this.status === 'open' || this.status === 'connecting') return;
    this.connectNow();
  }

  stop(): void {
    this.wanted = false;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.clearHandshake();
    this.ws?.close(1000, 'bye');
    this.ws = null;
    this.everConnected = false;
    this.setStatus('closed');
  }

  /** Drops the current socket (even if it still looks open) and connects again right away. */
  forceReconnect(): void {
    if (!this.wanted) return;
    const old = this.ws;
    this.ws = null;
    if (this.pingTimer) clearInterval(this.pingTimer);
    for (const [, p] of this.pending) {
      clearTimeout(p.timer);
      p.resolve({ ok: false, error: { code: 'NETWORK', message: 'Reconnecting' } });
    }
    this.pending.clear();
    try {
      old?.close(4000, 'stale');
    } catch {
      /* ignore */
    }
    this.attempts = 0;
    this.connectNow();
  }

  private clearHandshake(): void {
    if (this.handshakeTimer) clearTimeout(this.handshakeTimer);
    this.handshakeTimer = null;
  }

  /** True when the socket claims to be open but nothing has arrived for longer than the ping interval allows. */
  get looksStale(): boolean {
    return this.status !== 'open' || Date.now() - this.lastMessageAt > 20_000;
  }

  /** Resolves once the socket is authenticated and open (or false after `timeoutMs`). */
  whenOpen(timeoutMs = 8000): Promise<boolean> {
    if (this.status === 'open') return Promise.resolve(true);
    return new Promise((resolve) => {
      const done = (ok: boolean) => {
        clearTimeout(timer);
        this.openWaiters = this.openWaiters.filter((w) => w !== onOpen);
        resolve(ok);
      };
      const onOpen = () => done(true);
      const timer = setTimeout(() => done(false), timeoutMs);
      this.openWaiters.push(onOpen);
    });
  }

  private connectNow(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.clearHandshake();
    if (!navigator.onLine) {
      this.setStatus('offline');
      return;
    }
    // Drop a previous attempt that is still pending so its late events can't interfere.
    const previous = this.ws;
    if (previous && previous.readyState !== WebSocket.CLOSED) {
      this.ws = null;
      try {
        previous.close();
      } catch {
        /* ignore */
      }
    }
    this.setStatus(this.everConnected ? 'reconnecting' : 'connecting');
    const ws = new WebSocket(WS_URL);
    this.ws = ws;
    this.handshakeTimer = setTimeout(() => {
      this.handshakeTimer = null;
      if (this.ws !== ws || this.status === 'open') return;
      this.ws = null;
      try {
        ws.close();
      } catch {
        /* ignore */
      }
      if (this.wanted) this.scheduleReconnect();
    }, 10_000);
    ws.onopen = async () => {
      let token: string | null = null;
      try {
        token = await this.getToken();
      } catch {
        /* offline or the refresh failed: treated like no token, and retried */
      }
      if (this.ws !== ws) return;
      if (!token) {
        ws.close(4401, 'no token');
        return;
      }
      ws.send(JSON.stringify({ t: 'auth.v1', d: { token, clientVersion: '0.1.0' } }));
    };
    ws.onmessage = (ev) => this.onMessage(ev.data as string);
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.clearHandshake();
      if (this.pingTimer) clearInterval(this.pingTimer);
      for (const [, p] of this.pending) {
        clearTimeout(p.timer);
        p.resolve({ ok: false, error: { code: 'NETWORK', message: 'Connection lost' } });
      }
      this.pending.clear();
      if (ev.code === 4001) {
        this.setStatus('replaced');
        this.wanted = false;
        return;
      }
      if (ev.code === 4003) {
        this.setStatus('closed');
        this.wanted = false;
        return;
      }
      if (!this.wanted) return;
      this.scheduleReconnect();
    };
    ws.onerror = () => undefined;
  }

  private scheduleReconnect(): void {
    this.setStatus(navigator.onLine ? 'reconnecting' : 'offline');
    if (this.retryTimer) clearTimeout(this.retryTimer);
    const delay = Math.min(10000, 500 * 2 ** this.attempts) * (0.7 + Math.random() * 0.6);
    this.attempts++;
    this.retryTimer = setTimeout(() => this.connectNow(), delay);
  }

  private onMessage(raw: string): void {
    this.lastMessageAt = Date.now();
    let env: ServerEnvelope;
    try {
      env = JSON.parse(raw) as ServerEnvelope;
    } catch {
      return;
    }
    if (env.t === 'auth.ok.v1') {
      const d = env.d as { serverTime: number };
      this.serverOffsetMs = d.serverTime - Date.now();
      const wasReconnect = this.everConnected;
      this.everConnected = true;
      this.attempts = 0;
      this.clearHandshake();
      this.setStatus('open');
      this.startPing();
      const waiters = this.openWaiters;
      this.openWaiters = [];
      waiters.forEach((w) => w());
      if (wasReconnect) this.reconnectListeners.forEach((l) => l());
    } else if (env.t === 'ack.v1') {
      const d = env.d as { id: string; ok: boolean; data?: unknown; error?: { code: string; message: string } };
      const p = this.pending.get(d.id);
      if (p) {
        clearTimeout(p.timer);
        this.pending.delete(d.id);
        p.resolve({ ok: d.ok, data: d.data, error: d.error });
      }
    } else if (env.t === 'sys.pong.v1') {
      const d = env.d as { ts: number; serverTime: number };
      this.rttMs = Date.now() - d.ts;
      this.serverOffsetMs = d.serverTime + this.rttMs / 2 - Date.now();
    }
    this.handlers.get(env.t)?.forEach((h) => h(env.d, env));
    this.handlers.get('*')?.forEach((h) => h(env.d, env));
  }

  private startPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.lastMessageAt = Date.now();
    const ping = () => {
      // Two missed pongs in a row: the connection is dead even if the browser hasn't noticed yet.
      if (Date.now() - this.lastMessageAt > 35_000) {
        this.forceReconnect();
        return;
      }
      this.send('sys.ping.v1', { ts: Date.now() });
    };
    ping();
    this.pingTimer = setInterval(ping, 15000);
  }

  send(t: string, d: unknown, id?: string): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify({ t, d, ...(id ? { id } : {}) }));
    return true;
  }

  request(t: string, d: unknown, timeoutMs = 8000): Promise<{ ok: boolean; data?: unknown; error?: { code: string; message: string } }> {
    const id = uuid();
    return new Promise((resolve) => {
      if (this.status !== 'open' || !this.send(t, d, id)) {
        resolve({ ok: false, error: { code: 'NETWORK', message: 'Not connected' } });
        return;
      }
      const timer = setTimeout(() => {
        this.pending.delete(id);
        resolve({ ok: false, error: { code: 'TIMEOUT', message: 'The server did not respond in time' } });
      }, timeoutMs);
      this.pending.set(id, { resolve, timer });
    });
  }

  serverNow(): number {
    return Date.now() + this.serverOffsetMs;
  }
}
